import { useCallback } from 'react';

import { useQuerySnackbar } from '@explorer/hooks';
import {
  CRACRAPagedResponse,
  CRSFProcessPagedResponse,
} from '@explorer/open-api-client';
import { useQueryClient } from '@tanstack/react-query';

import { DEFAULT_LOCALE, parseCraCurrency } from '../constant';

const predicate = (queryKey, querySearch) => (query) =>
  JSON.stringify(query.queryKey).includes(queryKey) &&
  (!querySearch || JSON.stringify(query.queryKey).includes(querySearch));

export const useQueryDataScanningList = (querySearch, formik) => {
  const queryClient = useQueryClient();
  const { snackSuccess, snackError } = useQuerySnackbar();

  const handleFetchData = useCallback(() => {
    const queryKey = 'v1CraWebCrsfEntityListGet';

    const fetchedCRAScans:
      | CRSFProcessPagedResponse
      | CRACRAPagedResponse
      | undefined = queryClient.getQueryData([queryKey], {
      exact: false,
      type: 'active',
      predicate: predicate(queryKey, querySearch),
    });

    if (fetchedCRAScans && fetchedCRAScans.results.length > 0) {
      const craData = fetchedCRAScans.results[0];

      formik.setValues({
        entity_name: craData.entity_name,
        domains: craData.domains.join(','),
        ip_addresses: craData.ip_addresses?.join(',') || '',
        industry: `${craData?.metrics?.industry}` || '',
        number_of_employees:
          (craData?.metrics?.number_of_employees as number) || 0,
        number_of_pii_records:
          (craData?.metrics?.number_of_pii_records as number) || 0,
        company_revenue: (craData?.metrics?.company_revenue as number) || 0,
        locale: (craData?.locale as string) ?? DEFAULT_LOCALE,
        currency: parseCraCurrency(
          'currency' in craData ? craData.currency : null,
        ),
        cobranded: null,
        scan_opts: {
          force: false,
        },
      });

      snackSuccess('Fetch CRA Data Success');
    } else {
      snackError('Fetch CRA Data Error', 'No data found');
    }
  }, [formik, queryClient, querySearch, snackError, snackSuccess]);

  const invalidateQueries = useCallback(() => {
    return queryClient.invalidateQueries(['v1CraWebCrsfEntityListGet']);
  }, [queryClient]);

  return { handleFetchData, invalidateQueries };
};
